import type { ParsedData } from '../../../interfaces/toolData';
import { SmartDmSurveyParser } from './smartdm/SmartDmSurveyParser';
import { SmartDmTelemParser } from './smartdm/SmartDmTelemParser';
import { SmartDmSystemParser } from './smartdm/SmartDmSystemParser';
import { SmartDmErrorParser } from './smartdm/SmartDmErrorParser';
import { SmartDmGammaParser } from './smartdm/SmartDmGammaParser';
import { SmartDmTempParser } from './smartdm/SmartDmTempParser';
import { SmartDmVoltagesParser } from './smartdm/SmartDmVoltagesParser';
import { SmartDmPulseParser } from './smartdm/SmartDmPulseParser';
import { SmartDmRouteParser } from './smartdm/SmartDmRouteParser';
import { SmartDmRotationParser } from './smartdm/SmartDmRotationParser';
import { SmartDmShkVibeParser } from './smartdm/SmartDmShkVibeParser';
import { SmartDmDebugParser } from './smartdm/SmartDmDebugParser';
import { SmartDmRotGammaParser } from './smartdm/SmartDmRotGammaParser';

/**
 * Main entry point for JAE (Japan Aviation Electronics) tool parsers
 * Currently supports Smart DM product line
 */
export class JaeParser {
  /**
   * Parse JAE file content based on file type detection
   */
  static parse(fileContent: string, filename: string): ParsedData {
    // Extract file type from filename
    const fileType = this.extractFileType(filename);
    
    if (!fileType) {
      throw new Error(`Unable to determine JAE file type from filename: ${filename}`);
    }
    
    // Route to appropriate parser based on file type
    switch (fileType) {
      case 'SURVEY':
        return SmartDmSurveyParser.parse(fileContent);
      
      case 'TELEM':
        return SmartDmTelemParser.parse(fileContent);
      
      case 'SYSTEM':
        return SmartDmSystemParser.parse(fileContent);
        
      case 'ERROR':
        return SmartDmErrorParser.parse(fileContent);
      
      case 'GAMMA':
        return SmartDmGammaParser.parse(fileContent);
        
      case 'TEMP':
        return SmartDmTempParser.parse(fileContent);
        
      case 'VOLTAGES':
        return SmartDmVoltagesParser.parse(fileContent);
        
      case 'PULSE':
        return SmartDmPulseParser.parse(fileContent);
        
      case 'ROUTE':
        return SmartDmRouteParser.parse(fileContent);
        
      case 'ROTATION':
        return SmartDmRotationParser.parse(fileContent);
        
      case 'SHKVIBE':
        return SmartDmShkVibeParser.parse(fileContent);
        
      case 'DEBUG':
        return SmartDmDebugParser.parse(fileContent);
      
      case 'ROTGAMMA':
        return SmartDmRotGammaParser.parse(fileContent);
      
      default:
        throw new Error(`Unsupported JAE file type: ${fileType}`);
    }
  }

  /**
   * Check if file content appears to be a JAE Smart DM file
   */
  static canParse(fileContent: string): boolean {
    return fileContent.trim().startsWith('SmartDM');
  }

  /**
   * Extract file type from Smart DM filename
   * Example: SmartDM_0288-2-0147_SURVEY_20250708134919.csv -> SURVEY
   */
  static extractFileType(filename: string): string | null {
    const match = filename.match(/SmartDM_[^_]+_([^_]+)_\d+\.csv$/i);
    return match ? match[1].toUpperCase() : null;
  }
} 